import { useScan } from './ScanLayout';
import { PageHeader, Section, Banner, KV } from '../components/Section';

export function WhoisPage() {
  const scan = useScan();
  const m = scan.modules.whois;

  if (!m || !m.found) {
    if (m?.rate_limited) {
      return (
        <>
          <PageHeader title="WHOIS / RDAP" subtitle="Registry record" />
          <Section accent="amber">
            <Banner tone="warn" title="Registry rate-limits public RDAP from this service">
              {m.registry ?? 'The TLD registry'} blocks anonymous RDAP queries
              from shared cloud-edge IPs. This is the registry's policy, not a
              missing record — the domain almost certainly exists. For full
              registration details, look it up directly at{' '}
              <a href="https://lookup.icann.org/" target="_blank" rel="noreferrer"
                 className="underline">lookup.icann.org</a>.
            </Banner>
          </Section>
        </>
      );
    }
    return (
      <>
        <PageHeader title="WHOIS / RDAP" subtitle="Registry record" />
        <Section accent="amber">
          <Banner tone="warn" title="No RDAP record returned for this domain">
            The TLD's registry didn't return a record. Either the domain
            doesn't exist, or the registry doesn't operate a public RDAP
            server. Look it up directly at{' '}
            <a href="https://lookup.icann.org/" target="_blank" rel="noreferrer"
               className="underline">lookup.icann.org</a>.
          </Banner>
        </Section>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Domain Registration"
        subtitle="Registrar, registration and expiry dates, and authoritative nameservers"
      />

      <Section title="Registration" accent="blue">
        <div className="grid gap-4 sm:grid-cols-2">
          <KV k="Registrar" v={m.registrar ?? '—'} />
          {m.registry && <KV k="Registry" v={m.registry} />}
          <KV k="Registered" v={fmt(m.registered)} />
          <KV k="Last Updated" v={fmt(m.updated)} />
          <KV k="Expires" v={fmt(m.expires)} />
          {m.age_days != null && (
            <KV k="Domain Age" v={
              <span className={m.age_days < 90 ? 'text-amber-600' : 'text-ink'}>
                {fmtAge(m.age_days)}{m.age_days < 90 ? ' — recently registered' : ''}
              </span>
            } />
          )}
        </div>
        {m.age_days != null && m.age_days < 30 && (
          <div className="mt-3">
            <Banner tone="warn" title="Very young domain">
              Registered under 30 days ago. Newly-registered domains are disproportionately used for
              phishing and abuse — treat mail and links from it with extra caution. (Age alone is not proof of anything.)
            </Banner>
          </div>
        )}
        {m.raw_url && (
          <p className="mt-3 text-xs">
            <a href={m.raw_url} target="_blank" rel="noreferrer"
               className="text-accent hover:text-sentinel-900">View raw RDAP record ↗</a>
          </p>
        )}
      </Section>

      <Section title="Status" accent="blue">
        <div className="flex flex-wrap gap-1.5">
          {m.status.length > 0
            ? m.status.map(s => <span key={s} className="pill pill-info">{s}</span>)
            : <span className="text-sm text-ink-3">—</span>}
        </div>
      </Section>

      <Section title="Nameservers" accent="blue">
        {m.nameservers.length > 0 ? (
          <ul className="space-y-1 font-mono text-sm">
            {m.nameservers.map(n => <li key={n} className="text-ink-2">{n}</li>)}
          </ul>
        ) : <span className="text-sm text-ink-3">—</span>}
      </Section>
    </>
  );
}

function fmt(d?: string): string {
  if (!d) return '—';
  try { return new Date(d).toUTCString().replace(' GMT', ' UTC'); }
  catch { return d; }
}

function fmtAge(days: number): string {
  if (days < 60) return `${days} day${days === 1 ? '' : 's'}`;
  const years = days / 365;
  if (years < 1) return `${Math.round(days / 30)} months`;
  return `${years.toFixed(1)} years`;
}
